import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthGuard } from '@core/guards/auth.guard';
import { LanguagesGuard } from '@core/guards/languages.guard';
import { MODULE_KEY, PermissionEnum } from '@interfaces';
import { LayoutComponent } from '@layout/layout.component';
import { ActivateAccountComponent } from './activate-account/activate-account.component';
import { LoginComponent } from './login/login.component';
import { ResetPasswordComponent } from './reset-password/reset-password.component';
import { UserSettingsComponent } from './user-settings/user-settings.component';

import { IsForeignerGuard } from '@core/guards/is-foreigner.guard';
import { I18nLazyloadedResolver } from '@shared/i18n/i18n-lazyloaded-resolver';
import { AccountDeletedComponent } from './account-deleted/account-deleted.component';
import { ChatMobileComponent } from './chat/chat-mobile/chat-mobile.component';
import { ChatMobileUploadGuard } from './chat/chat-mobile/chat-mobile-upload.guard';
import { NewAccountComponent } from './new-account/new-account.component';
import { PrivacyPolicyComponent } from './privacy-policy/privacy-policy.component';
import { TOSComponent } from './tos/tos.component';
import { UserProcessFormComponent } from './user-process-form/user-process-form.component';
import { NoProcessesAvailableComponent } from './no-processes-available/no-processes-available.component';
import { InitUserComponent } from './init-user/init-user.component';
import { FeatureModuleGuard } from '@core/guards/feature-module.guard';

const routes: Routes = [
  {
    path: 'chat',
    component: ChatMobileComponent,
    canActivate: [AuthGuard],
    canDeactivate: [ChatMobileUploadGuard],
    resolve: { translations: I18nLazyloadedResolver },
    data: { translationsNames: ['auth-shared', 'selects-values'] },
  },
  {
    path: '',
    canActivate: [AuthGuard],
    canDeactivate: [],
    component: LayoutComponent,
    resolve: { translations: I18nLazyloadedResolver },
    data: { translationsNames: ['auth-shared', 'selects-values'] },
    children: [
      {
        path: 'user-settings',
        component: UserSettingsComponent,
        canActivate: [AuthGuard, LanguagesGuard],
        resolve: { translations: I18nLazyloadedResolver },
        data: {
          requiredPermissions: [PermissionEnum.CAN_EDIT_OWN_DATA],
          translationsNames: ['foreigner', 'management'],
        },
      },
      {
        path: 'user-process-form',
        component: UserProcessFormComponent,
        canActivate: [AuthGuard],
        data: { requiredPermissions: [PermissionEnum.CAN_EDIT_OWN_DATA] },
      },
      {
        path: 'user-process',
        loadChildren: () => import('./user-process/user-process.module').then(m => m.UserProcessModule),
        resolve: { translations: I18nLazyloadedResolver },
        data: { translationsNames: ['foreigner', 'user-process-summary'] },
      },
      {
        path: 'my-work-permit',
        loadChildren: () => import('./my-work-permit/my-work-permit.module').then(m => m.MyWorkPermitModule),
        canActivate: [AuthGuard, FeatureModuleGuard],
        resolve: { translations: I18nLazyloadedResolver },
        data: {
          moduleKey: MODULE_KEY.WORK_PERMIT,
          translationsNames: ['work-permits'],
          requiredPermissions: [PermissionEnum.CAN_EDIT_OWN_DATA],
        },
      },
      {
        path: 'no-processes-available',
        component: NoProcessesAvailableComponent,
        canActivate: [AuthGuard],
        data: { requiredPermissions: [PermissionEnum.CAN_EDIT_OWN_DATA] },
      },
      {
        path: 'management',
        loadChildren: () => import('./management/management.module').then(m => m.ManagementModule),
        resolve: { translations: I18nLazyloadedResolver },
        data: { translationsNames: ['management'] },
      },
      {
        path: 'tasks',
        loadChildren: () => import('./tasks/tasks.module').then(m => m.TasksModule),
        resolve: { translations: I18nLazyloadedResolver },
        data: { translationsNames: ['management', 'tasks'] },
      },
      {
        path: 'tracking',
        loadChildren: () => import('./tracking/tracking.module').then(m => m.TrackingModule),
        resolve: { translations: I18nLazyloadedResolver },
        data: { translationsNames: ['management', 'tracking'] },
      },
      {
        path: 'work-permits',
        loadChildren: () => import('./work-permits/work-permits.module').then(m => m.WorkPermitsModule),
        resolve: { translations: I18nLazyloadedResolver },
        data: { translationsNames: ['management', 'work-permits'] },
      },
      {
        path: 'cvm',
        loadChildren: () => import('./cvm/cvm.module').then(m => m.CVMModule),
        resolve: { translations: I18nLazyloadedResolver },
        canActivate: [FeatureModuleGuard],
        data: {
          moduleKey: MODULE_KEY.CVM,
          translationsNames: ['cvm', 'management', 'work-permits'],
          requiredPermissions: [PermissionEnum.CAN_VIEW_CANDIDATE_VERIFICATIONS],
        },
      },
      {
        path: 'manager',
        loadChildren: () => import('./manager/manager.module').then(m => m.ManagerModule),
        resolve: { translations: I18nLazyloadedResolver },
        data: { translationsNames: ['manager'] },
      },
      {
        path: 'administration',
        loadChildren: () => import('./administration/administration.module').then(m => m.AdministrationModule),
        resolve: { translations: I18nLazyloadedResolver },
        data: { translationsNames: ['administration', 'management'] },
      },
      {
        path: 'purchase',
        canActivate: [IsForeignerGuard],
        loadChildren: () => import('./purchase/purchase.module').then(m => m.PurchaseModule),
        resolve: { translations: I18nLazyloadedResolver },
        data: { translationsNames: ['purchase'] },
      },
      {
        path: 'partners',
        loadChildren: () => import('./partners/partners.module').then(m => m.PartnersModule),
        canActivate: [AuthGuard],
        resolve: { translations: I18nLazyloadedResolver },
        data: {
          translationsNames: ['management', 'foreigner'],
          requiredPermissions: [PermissionEnum.CAN_MANAGE_USERS_PARTNER_ACCOUNT],
        },
      },
    ],
  },
  {
    path: 'back-office',
    loadChildren: () => import('./back-office/back-office.module').then(m => m.BackOfficeModule),
    resolve: { translations: I18nLazyloadedResolver },
    data: { translationsNames: ['auth-shared', 'back-office', 'management'] },
  },
  {
    path: 'cdof',
    loadChildren: () => import('./candidate-open-form/candidate-open-form.module').then(m => m.CandidateOpenFormModule),
  },

  { path: 'reset-password', component: ResetPasswordComponent },
  { path: 'activate-account', component: ActivateAccountComponent },
  { path: 'login', component: LoginComponent },
  { path: 'new-account', component: NewAccountComponent },
  { path: 'account-deleted', component: AccountDeletedComponent },
  { path: 'init-user', component: InitUserComponent },
  { path: 'privacy-policy', component: PrivacyPolicyComponent },
  { path: 'tos', component: TOSComponent },

  { path: '**', redirectTo: '' },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { canceledNavigationResolution: 'computed' })],
  exports: [RouterModule],
})
export class AppRoutingModule {}
